import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { isEmbed, isNativeWindow } from '@local/common-web';
import { getExtensionByFileName, getFileNameWithoutExt } from '@local/ts-infra';
import { UntilDestroy } from '@ngneat/until-destroy';
import { EmbedService } from '@shared/embed.service';
import { LogService, WindowService } from '@shared/services';
import { Logger } from '@unleash-tech/js-logger';
import { SelectedItem } from 'src/app/bar/components/selected-item-popup/selected-item-popup.component';
import { ExperiencesService } from 'src/app/bar/services/experiences.service';
import { QuestionnaireService } from 'src/app/bar/services/questionnaire.service';
import { QuestionnaireData } from '../questionnaire.component';
import { questionnaireContent } from '../questionnaire.content';

@UntilDestroy()
@Component({
  selector: 'upload-questionnaire',
  templateUrl: './upload-questionnaire.component.html',
  styleUrls: ['./upload-questionnaire.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadQuestionnaireComponent {
  readonly questionnaireContent = questionnaireContent;
  readonly acceptTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']; // 'xls', xlsx'
  readonly MAX_FILE_SIZE = 1024 * 1024 * 100;
  readonly FILE_UPLOAD_SIZE = { width: '440px', height: '132px' };
  readonly OPTIONAL_ASSISTANT = '*Optional, if no assistant is selected we will use all your existing links as data sources.';
  readonly SELECTED_PLACE_HOLDER = 'Select Assistant ';
  private data: QuestionnaireData = {};
  private logger: Logger;
  private cancelProcess: boolean;
  file: File;
  uploadFailed: boolean;
  incorrectContent: boolean;
  loadingFile: boolean;
  disabledButton = true;

  @Input() assistantsList: SelectedItem[];
  @Input() selectedAssistant: SelectedItem;

  @Output() onUploadFile = new EventEmitter<QuestionnaireData>();
  @Output() nextStep = new EventEmitter<QuestionnaireData>();
  @Output() onSelectedAssistant = new EventEmitter<string>();

  constructor(
    private cdr: ChangeDetectorRef,
    private questionnaireService: QuestionnaireService,
    private logService: LogService,
    private windowService: WindowService,
    private embedService: EmbedService,
    private assistantService: ExperiencesService
  ) {
    this.logger = this.logService.scope('UploadQuestionnaire');
  }

  async onUpload(file: File) {
    this.file = file;
    this.cancelProcess = false;
    this.data = {
      ...this.data,
      fileName: getFileNameWithoutExt(file.name),
      extension: getExtensionByFileName(file.name),
    };
    this.onUploadFile.emit(this.data);
    this.uploadFailed = this.incorrectContent = false;
    this.loadingFile = true;
    this.cdr.markForCheck();
    try {
      const response = await this.questionnaireService.loadFile(file);
      if (this.cancelProcess) {
        return;
      }
      if (response.worksheets?.length) {
        this.data = {
          ...this.data,
          blobId: response.blobId,
          worksheets: response.worksheets,
          assistantId: this.selectedAssistant?.id,
        };
        this.onUploadFile.emit(this.data);
        this.disabledButton = false;
      } else {
        this.incorrectContent = true;
      }
    } catch (error) {
      this.logger.error('failed to upload file', error);
      this.uploadFailed = true;
    }
    this.loadingFile = false;
    this.cdr.markForCheck();
  }

  onDeleteFile(cancelProcess?: boolean) {
    this.cancelProcess = cancelProcess;
    this.disabledButton = true;
    this.uploadFailed = this.incorrectContent = false;
    this.file = null;
    this.onUploadFile.emit();
    this.cdr.markForCheck();
  }

  continue() {
    if (this.disabledButton || !this.data) {
      return;
    }
    this.nextStep.emit(this.data);
  }

  onSelectItem(item: SelectedItem) {
    if (!item) {
      this.selectedAssistant = null;
      this.cdr.markForCheck();
      return;
    }

    if (item.isNew) {
      return this.createAssistant(item.name);
    }

    if (item.id) {
      this.selectedAssistant = item;
      this.data.assistantId = this.selectedAssistant.id;

      this.assistantsList = this.assistantsList.filter((a) => a.id !== this.data.assistantId);
      this.assistantsList.unshift(this.selectedAssistant);
    }

    this.cdr.markForCheck();
  }

  async createAssistant(title: string) {
    const currentUrl = `/assistant/new/${title}`;
    if (isEmbed()) {
      this.embedService.openUrl(currentUrl);
    } else if (isNativeWindow()) {
      this.windowService.openNative(currentUrl);
    } else {
      this.questionnaireService.closePopup();
      await this.assistantService.createAssistant({ experienceType: 'rfp', settings: { title } });
    }
  }
}
